import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, ClipboardX, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function LeaveApprovalsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null); // {leave, action}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leave/requests", { params: { status, page_size: 50 } });
      setItems(data.items || []);
      const uids = [...new Set((data.items || []).map((r) => r.user_id))];
      if (uids.length) {
        const { data: u } = await api.get("/users", { params: { page_size: 100 } });
        const m = {}; (u.items || []).forEach((x) => { m[x.id] = x; });
        setUsers(m);
      }
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="leave-approvals-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Leave approvals" }]}
        title="Leave approvals"
        subtitle="Pending leave requests from your reporting hierarchy."
        favorite={{ key: "admin.leave-approvals", type: "screen", label: "Leave approvals", route: "/leave-approvals" }}
        testid="leaveapprovalspage-page-header"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Leave approvals</h1>
          <p className="text-sm text-neutral-500">Review and approve/reject leave requests from your team.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="approvals-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="approvals-list">
        {loading && <div className="py-10 text-center text-neutral-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="py-10 text-center text-neutral-400">
            <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
            No {status} requests
          </div>
        )}
        {items.map((r) => {
          const u = users[r.user_id];
          return (
            <Card key={r.id} className="border-neutral-200 rounded-2xl" data-testid={`approval-${r.id.slice(0, 8)}`}>
              <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[11px] uppercase text-neutral-400 tracking-widest">
                    {u?.role_code || "—"}
                  </div>
                  <div className="text-base font-bold text-neutral-900">{u?.name || r.user_id}</div>
                  <div className="text-sm text-neutral-500 mt-1">
                    <span className="font-semibold text-neutral-800">{r.leave_type_code.toUpperCase()}</span>
                    {" · "}{r.days_count}d
                    {" · "}<span className="font-mono text-xs">{r.from_date}</span> → <span className="font-mono text-xs">{r.to_date}</span>
                  </div>
                  {r.reason && (
                    <div className="text-[13px] text-neutral-600 mt-1 italic">"{r.reason}"</div>
                  )}
                  {r.approver_notes && r.status !== "pending" && (
                    <div className="text-[13px] text-neutral-500 mt-1">Decision note: {r.approver_notes}</div>
                  )}
                </div>
                {r.status === "pending" ? (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setDeciding({ leave: r, action: "reject" })}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`approval-reject-${r.id.slice(0, 8)}`}>
                      <ClipboardX className="h-4 w-4 mr-2" /> Reject
                    </Button>
                    <Button onClick={() => setDeciding({ leave: r, action: "approve" })}
                            className="tulsi-gradient text-white"
                            data-testid={`approval-approve-${r.id.slice(0, 8)}`}>
                      <ClipboardCheck className="h-4 w-4 mr-2" /> Approve
                    </Button>
                  </div>
                ) : (
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${{
                    approved: "bg-green-100 text-green-800",
                    rejected: "bg-red-100 text-red-700",
                    cancelled: "bg-neutral-100 text-neutral-600",
                  }[r.status]}`}>{r.status.toUpperCase()}</span>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {deciding && (
        <DecisionDialog leave={deciding.leave} action={deciding.action}
                        onClose={() => setDeciding(null)}
                        onDone={() => { setDeciding(null); load(); }} />
      )}
    </div>
  );
}

function DecisionDialog({ leave, action, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isReject = action === "reject";

  const submit = async () => {
    if (isReject && !notes.trim()) return toast.error("Rejection notes required");
    setBusy(true);
    try {
      await api.post(`/leave/${leave.id}/${action}`, { notes: notes.trim() || null });
      toast.success(action === "approve" ? "Leave approved" : "Leave rejected");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="approval-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isReject ? "Reject leave" : "Approve leave"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-neutral-500">
            {leave.leave_type_code.toUpperCase()} · {leave.days_count} days · {leave.from_date} → {leave.to_date}
          </div>
          <div>
            <label className="text-[11px] uppercase text-neutral-500 tracking-widest">
              Notes {isReject && <span className="text-red-500">*</span>}
            </label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder={isReject ? "Reason for rejection…" : "Optional notes…"}
                      data-testid="approval-decision-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className={isReject ? "bg-red-600 text-white hover:bg-red-700" : "tulsi-gradient text-white"}
                  data-testid="approval-decision-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isReject ? "Reject" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
